import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

interface JobType {
  type: string;
  eligible: number;
  cost_per_word_usd: number;
  estimated_usd: number;
}

interface SweepJob {
  id: string;
  type: string;
  cap_usd: number;
  slice_size: number;
  status: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  spent_usd: number;
  processed: number;
  remaining: number;
  stopped_reason: string | null;
  last_error: string | null;
}

interface SweepLogRow {
  id: string;
  job_id: string;
  started_at: string;
  finished_at: string | null;
  processed: number;
  spent_usd: number;
  remaining: number;
  note: string | null;
  trigger: string | null;
}

interface SweepWindow {
  start_hour: number;
  end_hour: number;
  tz: string;
  enabled: boolean;
}

interface SweepResponse {
  types: JobType[];
  jobs: SweepJob[];
  log: SweepLogRow[];
  window: SweepWindow;
  inside_window: boolean;
  default_slice: number;
  cost_per_word_usd: number;
}

const LABELS: Record<string, string> = {
  migrate_game: 'Migrate Game words from the Airtable CSV export',
  stage1_game: 'Stage 1 — queued Game words',
  verify_facts: 'Verify Related fun facts',
  regenerate_failures: 'Regenerate audit failures',
  audit_related: 'Audit un-audited Related words',
};

const ACTIVE = new Set(['queued', 'running']);

function money(n: number | null | undefined): string {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

function when(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

export default function RunsView() {
  const [data, setData] = useState<SweepResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caps, setCaps] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = (await api.get('/api/sweep')) as SweepResponse;
      setData(res);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while anything is active, so progress and ledger spend move on screen.
  useEffect(() => {
    const active = data?.jobs?.some((j) => ACTIVE.has(j.status));
    if (!active) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    if (timer.current) return;
    timer.current = window.setInterval(load, 10000);
    return () => {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    };
  }, [data, load]);

  const run = useCallback(async (type: string) => {
    const cap = Number(caps[type]);
    if (!Number.isFinite(cap) || cap <= 0) {
      setError('Set a cap in dollars before running.');
      return;
    }
    setBusy(type);
    try {
      await api.post('/api/sweep/jobs', { type, capUsd: cap, sliceSize: data?.default_slice ?? 3 });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [caps, data, load]);

  const stop = useCallback(async (id: string) => {
    setBusy(id);
    try {
      await api.post(`/api/sweep/jobs/${id}/stop`, {});
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [load]);

  const runSlice = useCallback(async () => {
    setBusy('slice');
    try {
      await api.post('/api/sweep/run', { trigger: 'manual' });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [load]);

  return (
    <div className="wb-runs">
      {error && <div className="wb-error">{error}</div>}
      {loading && !data && <div className="wb-empty">Loading runs…</div>}

      {data && (
        <>
          <div className="wb-filters">
            <span className="wb-totals">
              <span>
                sweep window <b>{String(data.window.start_hour).padStart(2, '0')}:00–
                {String(data.window.end_hour).padStart(2, '0')}:00</b> {data.window.tz}
              </span>
              <span>{data.inside_window ? 'inside window now' : 'outside window now'}</span>
              <span>{data.window.enabled ? 'enabled' : 'DISABLED'}</span>
              <span>measured <b>{money(data.cost_per_word_usd)}</b>/word</span>
            </span>
            <button className="wb-refresh" onClick={load}>Refresh</button>
            <button className="wb-move" onClick={runSlice} disabled={busy === 'slice'}
              title="Run one slice of the oldest active job now, without waiting for the schedule">
              {busy === 'slice' ? 'Running…' : 'Run one slice now'}
            </button>
          </div>

          <table className="wb-table">
            <thead>
              <tr>
                <th className="wb-col-word">Run</th>
                <th>Eligible</th>
                <th>Estimated</th>
                <th>Cap ($)</th>
                <th className="wb-col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.types.map((t) => (
                <tr key={t.type} className="wb-row">
                  <td className="wb-col-word"><span className="wb-word">{LABELS[t.type] ?? t.type}</span></td>
                  <td><b>{t.eligible}</b> word{t.eligible === 1 ? '' : 's'}</td>
                  <td>{money(t.estimated_usd)}</td>
                  <td>
                    <input
                      className="wb-capinput"
                      type="number" min="0" step="1"
                      placeholder={String(Math.ceil(t.estimated_usd) || 1)}
                      value={caps[t.type] ?? ''}
                      onChange={(e) => setCaps((c) => ({ ...c, [t.type]: e.target.value }))}
                    />
                  </td>
                  <td className="wb-col-actions">
                    <button
                      className="wb-accept"
                      disabled={busy === t.type || t.eligible === 0}
                      onClick={() => run(t.type)}
                      title={t.eligible === 0 ? 'Nothing eligible' : 'Create a job and run one slice now'}
                    >
                      {busy === t.type ? 'Starting…' : 'Run'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="wb-detail-block wb-runs-block">
            <h4>Jobs</h4>
            {data.jobs.length === 0 && <p className="wb-empty">No runs yet.</p>}
            {data.jobs.length > 0 && (
              <table className="wb-table">
                <thead>
                  <tr>
                    <th>Type</th><th>Status</th><th>Processed</th><th>Remaining</th>
                    <th>Spent / cap</th><th>Created</th><th className="wb-col-actions">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.id} className="wb-row">
                      <td>{LABELS[j.type] ?? j.type}</td>
                      <td>
                        <span className={`wb-chip ${
                          j.status === 'done' ? 'wb-chip-pass'
                            : ACTIVE.has(j.status) ? 'wb-chip-override' : 'wb-chip-fail'}`}>
                          {j.status}
                        </span>
                        {j.stopped_reason && <div className="wb-meta">{j.stopped_reason}</div>}
                        {j.last_error && <div className="wb-error">{j.last_error}</div>}
                      </td>
                      <td>{j.processed}</td>
                      <td>{j.remaining}</td>
                      <td>{money(j.spent_usd)} / {money(j.cap_usd)}</td>
                      <td className="wb-meta">{when(j.created_at)}</td>
                      <td className="wb-col-actions">
                        <button className="wb-move" disabled={!ACTIVE.has(j.status) || busy === j.id}
                          onClick={() => stop(j.id)}>
                          {busy === j.id ? 'Stopping…' : 'Stop'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="wb-detail-block wb-runs-block">
            <h4>Last 10 slices</h4>
            {data.log.length === 0 && <p className="wb-empty">No slices have run yet.</p>}
            <ul className="wb-reasons">
              {data.log.map((l) => (
                <li key={l.id}>
                  <span className="wb-reason-axis">{when(l.started_at)}</span>
                  <span className="wb-chip wb-chip-pass">{l.processed}</span>
                  <span className="wb-reason-text">
                    {money(l.spent_usd)} spent · {l.remaining} left · {l.trigger ?? 'cron'}
                    {l.note ? ` · ${l.note}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
